import {
  CheckCircleOutline,
  ErrorOutlineOutlined,
  Warning,
} from "@mui/icons-material";
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { OrganizationAPI } from "../../../../api";
import Dialog from "../../../../components/Dialog";
import { useGlobalOrganizationContext } from "../../../../hooks/useGlobalOrganizationContext";
import { useAlert } from "../../../../lib/alert";
import { Organization } from "../../../../model";

export type OrganizationManualStripeDialogProps = {
  open: boolean;
  handleConfirm: () => Promise<void>;
  handleCancel: () => void;
  organization: Partial<Organization>;
  title?: string;
  confirmText?: string;
  cancelText?: string;
};

enum ValidationState {
  NA,
  LOADING,
  VALID,
  WARNING,
  INVALID,
}

const OrganizationManualStripeDialog = ({
  open,
  handleConfirm,
  handleCancel,
  organization,
  title,
  confirmText = "Save",
  cancelText = "Cancel",
}: OrganizationManualStripeDialogProps) => {
  const { organizationBillingState } = useGlobalOrganizationContext();

  const [validationState, setValidationState] = useState<ValidationState>(
    ValidationState.NA,
  );
  const [hasAgreed, setHasAgreed] = useState(false);
  const [stripeId, setStripeId] = useState<string | undefined>(
    organizationBillingState?.stripe_id,
  );

  const { success } = useAlert();

  useEffect(() => {
    if (stripeId == "") {
      setValidationState(ValidationState.NA);
      return;
    }

    if (!organization.id || !stripeId) {
      setValidationState(ValidationState.NA);
      return;
    }

    OrganizationAPI.validateStripeId(organization.id, stripeId).then(
      (result) => {
        setHasAgreed(false);
        if (!result.valid) {
          setValidationState(ValidationState.INVALID);
          return;
        }
        if (result.organization_id !== organization.id) {
          setValidationState(ValidationState.WARNING);
          return;
        }
        setValidationState(ValidationState.VALID);
      },
    );
  }, [organization.id, stripeId]);

  return (
    <Dialog
      open={open}
      title={title || "Setup Stripe"}
      confirmText={confirmText}
      cancelText={cancelText}
      handleConfirm={async () => {
        if (!organization.id || !stripeId) {
          handleCancel();
          return;
        }

        OrganizationAPI.updateStripeId(organization.id, stripeId || "")
          .then(() => {
            success("The organization's Stripe ID has been updated!");
            handleConfirm();
          })
          .catch((e) => {
            console.error(e);
            setValidationState(ValidationState.INVALID);
          });
      }}
      handleCancel={handleCancel}
      maxWidth="sm"
      fullWidth={true}
      disableConfirm={
        validationState === ValidationState.LOADING ||
        validationState === ValidationState.INVALID ||
        validationState === ValidationState.NA ||
        !hasAgreed
      }
    >
      <Typography variant="body1">
        You are manually setting up {organization.name} for Stripe, which
        requires a valid Stripe Customer ID.
      </Typography>
      <Box display={"flex"} alignItems={"center"} my={1}>
        <TextField
          autoFocus
          margin="dense"
          id="stripe-id"
          label={`Stripe Customer ID`}
          type="text"
          fullWidth
          variant="standard"
          value={stripeId}
          onChange={(e) => setStripeId(e.target.value as string)}
          sx={{
            flex: 1,
          }}
          error={validationState === ValidationState.INVALID}
        />
        {validationState === ValidationState.LOADING && (
          <CircularProgress size={24} sx={{ ml: 2 }} />
        )}
        {validationState === ValidationState.WARNING && (
          <>
            <Tooltip title="This Stripe ID is currently set to another organization.">
              <Warning
                sx={{
                  color: (theme) => theme.palette.warning.main,
                  ml: 2,
                }}
              />
            </Tooltip>
          </>
        )}
        {validationState === ValidationState.VALID && (
          <Tooltip title="This Stripe ID is valid.">
            <CheckCircleOutline
              sx={{
                color: (theme) => theme.palette.success.main,
                ml: 2,
              }}
            />
          </Tooltip>
        )}
        {validationState === ValidationState.INVALID && (
          <Tooltip title="This Stripe ID is invalid.">
            <ErrorOutlineOutlined
              sx={{
                color: (theme) => theme.palette.error.main,
                ml: 2,
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Collapse
        in={
          validationState === ValidationState.VALID ||
          validationState === ValidationState.WARNING
        }
      >
        <Box display={"flex"} alignItems={"center"} my={1} mt={2}>
          <Typography
            variant="caption"
            sx={{
              flex: 1,
            }}
          >
            I have checked that the Stripe ID properly corresponds to this
            organization and that billing has been setup correctly.
          </Typography>
          <Checkbox
            checked={hasAgreed}
            onChange={(e) => {
              setHasAgreed(e.target.checked);
            }}
          />
        </Box>
      </Collapse>
    </Dialog>
  );
};

export default OrganizationManualStripeDialog;
